import { CheckIcon, CircleDotIcon, Trash2Icon } from 'lucide-react';
import { createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Avatar } from '@/shared/components/avatar';
import { RelativeTime } from '@/shared/components/date';
import { cn } from '@/shared/lib/cn';
import { useModuleRegistry } from '@/shared/registry/context';
import { Button } from '@/shared/ui/button';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { useDeleteNotification, useMarkRead, type Notification } from '../api';
import { moduleLabel, renderNotificationText } from '../render';

import { ModuleIconBadge, ModuleIconTile } from './ModuleIcon';

/** The text of a notification: a module's custom renderer, else its i18n template, else the fallback text. */
export function NotificationText({ notification }: { notification: Notification }) {
  const { i18n } = useTranslation();
  const registry = useModuleRegistry();
  const Renderer = registry.getNotificationRenderer(notification.type);
  if (Renderer) return createElement(Renderer, { notification });
  return <>{renderNotificationText(notification, i18n)}</>;
}

function NotificationAvatar({ notification, size }: { notification: Notification; size: 'md' | 'lg' }) {
  if (!notification.actor) return <ModuleIconTile module={notification.module} size={size} />;
  return (
    <span className="relative inline-flex shrink-0">
      <Avatar user={notification.actor} size={size} />
      <ModuleIconBadge module={notification.module} />
    </span>
  );
}

export interface NotificationItemProps {
  notification: Notification;
  /** `compact` in the bell's dropdown, `full` on the notifications page. */
  variant?: 'compact' | 'full';
  /** Called after the notification was opened (e.g. to close the dropdown). */
  onOpened?: () => void;
}

/**
 * One notification: actor (or module) avatar, text, time and module; opening marks it read and follows its link.
 * Read/unread and delete are secondary actions (revealed on hover where hover exists, always visible on touch).
 */
export function NotificationItem({ notification, variant = 'compact', onOpened }: NotificationItemProps) {
  const { t, i18n } = useTranslation('notifications');
  const navigate = useNavigate();
  const markRead = useMarkRead();
  const remove = useDeleteNotification();
  const unread = !notification.isRead;
  const others = notification.actors.filter((a) => a.id !== notification.actor?.id);

  const open = () => {
    if (unread) markRead.mutate({ id: notification.id, read: true });
    if (notification.link) void navigate(notification.link);
    onOpened?.();
  };

  return (
    <div
      data-testid="notification-item"
      data-unread={unread || undefined}
      data-type={notification.type}
      className={cn(
        'group/item relative flex items-start gap-2 rounded-lg transition-colors',
        variant === 'compact' ? 'px-2 py-2.5' : 'px-3 py-3 sm:px-4',
        'focus-within:bg-accent/40 hover:bg-accent/60',
      )}
    >
      <button
        type="button"
        onClick={open}
        className="flex min-w-0 flex-1 items-start gap-3 rounded-md text-left focus-ring"
      >
        <NotificationAvatar notification={notification} size={variant === 'compact' ? 'md' : 'lg'} />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block text-sm leading-snug break-words',
              variant === 'compact' && 'line-clamp-3',
              unread ? 'font-medium text-foreground' : 'text-foreground/85',
            )}
          >
            <NotificationText notification={notification} />
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
            <RelativeTime date={notification.createdAt} />
            <span aria-hidden>·</span>
            <span className="truncate">{moduleLabel(notification.module, i18n)}</span>
            {notification.count > 1 && (
              <span
                className="rounded-full bg-muted px-1.5 py-px font-medium text-muted-foreground tabular-nums"
                title={
                  others.length > 0
                    ? t('item.alsoBy', { names: others.map((a) => a.fullName).join(', ') })
                    : undefined
                }
              >
                {t('item.more', { count: notification.count - 1 })}
              </span>
            )}
          </span>
        </span>
      </button>

      <div className="flex shrink-0 items-center gap-0.5 self-center">
        <div className="flex items-center gap-0.5 transition-opacity group-focus-within/item:opacity-100 group-hover/item:opacity-100 can-hover:opacity-0">
          <SimpleTooltip content={unread ? t('item.markRead') : t('item.markUnread')}>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={unread ? t('item.markRead') : t('item.markUnread')}
              data-testid="notification-toggle-read"
              onClick={() => markRead.mutate({ id: notification.id, read: unread })}
            >
              {unread ? <CheckIcon /> : <CircleDotIcon />}
            </Button>
          </SimpleTooltip>
          <SimpleTooltip content={t('item.delete')}>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={t('item.delete')}
              data-testid="notification-delete"
              onClick={() => remove.mutate(notification.id)}
              className="hover:text-destructive-text"
            >
              <Trash2Icon />
            </Button>
          </SimpleTooltip>
        </div>
        <span
          className={cn('mx-1 size-2 shrink-0 rounded-full', unread ? 'bg-brand' : 'bg-transparent')}
          aria-label={unread ? t('item.unread') : undefined}
          role={unread ? 'img' : undefined}
        />
      </div>
    </div>
  );
}
